import { Enumerable, Kbo } from "@models/configurators/configurator-di";
import { CToken } from "@models/configurators/ctoken";
import { KbObjectManager } from "@models/configurators/kb-object-manager";
import { UiObject } from "@models/configurators/ui-object";
import { IField, IOption, ITag, IUploadValue } from "@models/entity-models";
import { eFieldControl, eFieldType, eFormatType, eRuleType, eSelectSource, eValidationTiming } from "@models/enums";
import { Utils } from "@tools";

export interface IFixThisOption {
    fix: () => any;
    label: string;
}

export interface IFieldValidationMsg {
    message: string;
    fixThisOptions?: IFixThisOption[];
}

@Kbo(CToken.Field)
export class Field extends UiObject implements IField {
    constructor(kom: KbObjectManager) {
        super(kom, { ruleType: eRuleType.field });

    }

    public type: string;
    public control: string;
    public helpMediaPath: string;
    public helpUrl: string;
    public validationErrors: IFieldValidationMsg[] = [];
    public warnings: IFieldValidationMsg[] = [];
    public hasBeenTouched: boolean; //tracks whether the field has been interacted with by the user since the configurator started
    // public valid: boolean = true;
    // public invalidReason: string;
    public enabled: boolean;
    public required: boolean;
    public searchable: boolean;
    // public visible: boolean;
    public precision: number;
    public constrainValue: boolean;
    public min: number;
    public max: number;
    public sliderAllowDirectEdit: boolean;
    public sliderValueVisibility: string;
    public showLabel: boolean;
    /**
     * in some numeric controls, we can constrain options to increments from min to max.
     */
    public step: number;
    public mask: string;
    public dateFormat: string;
    public format: string = eFormatType.none; // none, currency, percentage, number
    public formatPrefix: string;
    public formatSuffix: string;
    public formatCurrency: string;
    public formatDecimalSeparator: string;
    public formatThousandsSeparator: string;
    public formatMinPrecision: number;
    public acceptedFileTyes: string;
    /** Compatibility alias for the correctly-spelled API/editor property. */
    public get acceptedFileTypes(): string {
        return this.acceptedFileTyes;
    }
    public set acceptedFileTypes(value: string) {
        this.acceptedFileTyes = value;
    }
    public uploadToScene: boolean;
    public convertUploadedSceneImageToMesh: boolean;
    public uploadImageThreshold: number;
    public convertUploadPdfToImage: boolean;
    public linkToDynamicAttribute: boolean;
    public dynamicAttributeId: string;
    public _lockDynamicAttributeControl: boolean;
    public sortColumn: string;
    /**
     * v3 (v >= 3): organizational folder this field belongs to in the admin tree
     * (design spec §1.1/§1.2). Nullable; never affects runtime rendering. No
     * initializer so legacy (v < 3) fields don't gain an enumerable folderId in
     * their serialized JSON.
     */
    public folderId: string | null;

    private _tags: ITag[];
    @Enumerable()
    public get tags(): ITag[] {
        if (!this._tags) this._tags = [];
        return this._tags;
    }
    public set tags(val) {
        this._tags = val;
    }

    private convertValue(val: any) {
        return Field.convertToType(val, this.type, this.precision);
    }

    private convertForSet(val: any) {
        /* dates need special handling.  Coming from json, they will be ISO strings.  
        We need them to be Date objects so they play well with snap rules.  But we 
        can't new up a new Date() object in the field get() which is where we handle other conversions
        because it will break the binding system in angular (creates an endless digest).
        So we create the Date object in the field value setter. */
        if (this.type == eFieldType.date && val && !(val instanceof Date)) {
            val = new Date(val);
        }
        if (this.type == eFieldType.upload && (!val || typeof (val.path) == "undefined"))
        {
            val = <IUploadValue>{
                path: null,
                assetId: null
            };
        }

        return val;
    }

    private _value: any;
    private _lastCalculatedValue: any; // the last value calculated by get value()
    private _dirty: any; // meant to work in concert with $freezeFieldValueCalculation.  If marked to true, the field will recalculate the next time get value() is called even when frozen.
    @Enumerable()
    public get value(): any {
        let skip = this.$parentConfigurator && this.$parentConfigurator.$freezeFieldValueCalculation && !this._dirty;

        if (!skip) {
            let val = this.convertValue(this._value);
            if (this.$parentConfigurator && this.$parentConfigurator.$running && this.isValueConstrainedToOptions()) {
                if (this.isArrayType()) {
                    let shouldUpdate = false;
                    for (let v of val) {
                        if (!this.options.some(o => this.convertValue(o.value) == v)) {
                            shouldUpdate = true;
                            break;
                        }
                    }
                    if (shouldUpdate && this.options.length) { // prevent infinite $digest loop
                        val = val.filter(v => this.options.some(o => o.value == v));
                        this._value = val;
                    }
                } else if (!this.options.some(o => this.convertValue(o.value) == val)) {
                    val = this.options.length ? this.convertValue(this.options[0].value) : null;
                }
            }
            if (this.isArrayType() && this._value == null) {
                this._value = val;
            }
            this._lastCalculatedValue = val;
            this._dirty = false;
        }
        return this._lastCalculatedValue;
    }
    public set value(val) {
        let origValue = this.value;
        this._value = this.convertForSet(val);
        let newValue = this.value;
        if (origValue != newValue && this.$parentConfigurator) {
            this.$parentConfigurator.$fieldsDirty = true;
            this.$parentConfigurator.$fieldsDirtyForSceneRules = true;
        }
    }

    /**
     * gets the underlying value, not the one calculated by looking at the options.
     * This is needed for passing to the server when running option filters, so the option
     * filter can calculate it's own value for the field correctly and arrive at the same
     * conclusion as the client.
     */
    @Enumerable()
    public get desiredValue(): any {
        return this._value;
    }

    /**
     * if the field is a number, formattedValue will return a string representation of the value formatted
     * formatted per the format_ properties on the field
     */
    @Enumerable()
    public get formattedValue(): string {
        if (!this.value) {
            return "";
        } else if (this.type === eFieldType.number) {
            let args: INumberFormatArgs = {
                formatType: this.format,
                currency: this.formatCurrency,
                minPrecision: this.formatMinPrecision,
                maxPrecision: this.precision,
                decimalSeparator: this.formatDecimalSeparator,
                thousandsSeparator: this.formatThousandsSeparator,
                prefix: this.formatPrefix,
                suffix: this.formatSuffix
            };
            return (this.value as number).format(args);
        } else {
            return this.value.toString();
        }
    }

    /** meant to work with $freezeFieldValueCalculation.  Marks the field value to be recalculated the next time it's asked for */
    public recalculateValue() {
        this._dirty = true;
    }

    private _minDate: Date;
    @Enumerable()
    public get minDate(): Date {
        return Field.convertToType(this._minDate, eFieldType.date, 0);
    }
    public set minDate(val) {
        this._minDate = this.convertForSet(val);
    }

    private _maxDate: Date;
    @Enumerable()
    public get maxDate(): Date {
        return Field.convertToType(this._maxDate, eFieldType.date, 0);
    }
    public set maxDate(val) {
        this._maxDate = this.convertForSet(val);
    }

    /**
     * the dynamic list of options that the UI binds to.  If the selectSource is simple,
     * then these options are already filled in.  If the selectSource is optionFilter, then these
     * options are filled in dynamically on every field value change
     */
    private _options: IOption[];
    @Enumerable()
    public get options(): IOption[] {
        if (!this._options) this._options = [];
        return this._options;
    }
    public set options(val) {
        this._options = val;
        this.recalculateValue();
    }

    public sort: string;
    public selectSource: string;
    public idOptionFilter: string;
    public autoCompleteSource: string;
    public useFullTextSearch: boolean;
    public constrainToSource: boolean;
    public idTable: number;
    public dbTable: string;
    public valueColumn: string;
    public labelColumn: string;
    public imageColumn: string;
    public imageWhenSelectedColumn: string;
    public descriptionColumn: string;
    public imageHeight: number;
    public imageWidth: number;
    public imageOnly: boolean;
    public optionLayout: string;
    public dropdownOptionLayout: string;
    public enableSelectedImages: boolean;

    public actionJs: string;
    public actionBlocks: string;

    public iconStyle: string;
    public icon: string;
    public buttonStyle: string;

    public isValid(): boolean {
        let timing: string = eValidationTiming.always;
        if (this.$parentConfigurator) {
            timing = this.$parentConfigurator.validationTiming;
        }

        this.$valid = (timing == eValidationTiming.always || this.hasBeenTouched) ?
            (!this.validationErrors || this.validationErrors.length === 0)
            : true;

        return this.$valid;
    }

    public preValidate() {
        // clear validation errors
        this.validationErrors.clear();
        this.warnings.clear();

        // if number field
        if (this.type == eFieldType.number) {
            // validate the value is btw min & max
            if (this.constrainValue && Utils.isDefined(this.min) && this.value < this.min) {
                this.addValidation({
                    message: loc.validation_valuemustbegreaterthan.format(this.min.toString())
                });
            }
            if (this.constrainValue && Utils.isDefined(this.max) && this.value > this.max) {
                this.addValidation({
                    message: loc.validation_valuemustbelessthan.format(this.max.toString())
                });
            }
        } else if (this.type == eFieldType.date) {
            if (this.constrainValue && Utils.isDefined(this.minDate) && this.value < this.minDate) {
                this.addValidation({
                    message: loc.validation_valuemustbegreaterthan.format(this.minDate.toDateString())
                });
            }
            if (this.constrainValue && Utils.isDefined(this.maxDate) && this.value > this.maxDate) {
                this.addValidation({
                    message: loc.validation_valuemustbelessthan.format(this.maxDate.toDateString())
                });
            }
        }
        
        if (this.type == eFieldType.text || this.type == eFieldType.color || this.type == eFieldType.date) {
            if (!this.value && this.required) {
                this.addValidation({
                    message: loc.validation_required
                });
            }
        }
    }

    public validateCondition(condition: boolean, errorMessage: string, mode: string, fixThis: IFixThisOption[] = []) {
        if (!condition) {
            if (mode == "warning") {
                this.addValidation({ message: errorMessage, fixThisOptions: fixThis }, true);
            } else {
                this.addValidation({ message: errorMessage, fixThisOptions: fixThis });
            }
        }
    }

    public addValidation(msg: IFieldValidationMsg, isWarning: boolean = false) {
        //don't add if it's a duplicate
        if (isWarning) {
            if (!this.warnings.some(w => w.message == msg.message)) {
                if (!this.warnings) this.warnings = [];
                this.warnings.push(msg);
            }
        } else {
            if (!this.validationErrors.some(w => w.message == msg.message)) {
                if (!this.validationErrors) this.validationErrors = [];
                this.validationErrors.push(msg);
            }
        }
    }

    /**
     * returns whether a field is allowed to join a select table
     */

    // TODO: This logic is copied in Field.cs... the logic should be defined in only
    // Field.cs and picked up by codegen here.
    public canJoinOptionFilter(): boolean {
        if (this.control == eFieldControl.imageSelect ||
            this.control == eFieldControl.select ||
            this.control == eFieldControl.radio ||
            this.control == eFieldControl.colorSelect ||
            this.control == eFieldControl.swatchSelect ||
            this.control == eFieldControl.checkbox ||
            this.control == eFieldControl.toggle ||
            this.control == eFieldControl.multiSelect ||
            this.control == eFieldControl.imageMultiSelect ||
            this.control == eFieldControl.colorMultiSelect) {
            return true;
        } else {
            return false;
        }
    }

    public isValueConstrainedToOptions(): boolean {
        return (this.control == eFieldControl.select ||
            this.control == eFieldControl.radio ||
            this.control == eFieldControl.imageSelect ||
            this.control == eFieldControl.colorSelect ||
            this.control == eFieldControl.swatchSelect ||
            this.control == eFieldControl.multiSelect ||
            this.control == eFieldControl.imageMultiSelect ||
            this.control == eFieldControl.colorMultiSelect ||
            (
                (this.control == eFieldControl.checkbox || this.control == eFieldControl.toggle) &&
                this.selectSource == eSelectSource.optionFilter &&
                this.idOptionFilter != null
            )
        );
    }

    public isArrayType(): boolean {
        return (this.type.equalsAny(eFieldType.textArray, eFieldType.numberArray, eFieldType.colorArray));
    }

    public getBaseType(): string {
        if (this.isArrayType()) {
            return this.type.substr(0, this.type.length - 5);
        } else {
            return this.type;
        }
    }

    public static convertToType(value: any, toType: string, precision: number) {
        let val: any = value;

        if (value == null) {
            val = null;
        }

        if (toType === eFieldType.number) {
            // convert to a number
            // if (typeof value == "number" || value instanceof Number)
            //    return value;
            val = Number(value);
            // test if NaN : https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isNaN
            if (val != val) {
                val = 0;
            }
            // round to precision
            if (precision != -1) {
                val = val.rounder(precision);
            }
        } else if (toType === eFieldType.boolean) {
            if (typeof val === "boolean") {
                return val;
            }
            if (typeof val === "string" || val instanceof String) {
                val = Utils.areEqual(val as string, "true");
            } else {
                val = Boolean(val);
            }
        } else if (toType === eFieldType.date) {
            if (value != null && !(value instanceof Date)) {
                val = new Date(value);
            }
        } else if (toType == eFieldType.textArray ||
            toType == eFieldType.numberArray ||
            toType == eFieldType.colorArray
        ) {
            if (value == null) {
                val = [];
            } else if (!Array.isArray(value)) {
                val = [value];
            }
            // now we should have an array, make sure all items in the array are of the right type
            for (let i = 0; i < val.length; i++) {
                val[i] = Field.convertToType(val[i], toType.substr(0, toType.length - 5), precision);
            }
        }
        else if (toType == eFieldType.upload) {
            if (value == null || typeof (value.path) == 'undefined') {
                val = <IUploadValue>{
                    assetId: null,
                    path: null
                };
            }
        }

        return val;
    }
}
